import { PureComponent } from 'react';
import { Redirect, Route, Switch, RouteProps } from 'react-router';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SiteList from 'builder/scenes/SiteList';
import userManager from 'builder/util/userManager';
import { User } from 'shared/state/misc/oidc';
import SiteRouter from 'builder/routers/SiteRouter';
import { persistLocation } from 'shared/util/locationPersistence';
import Loading from 'builder/scenes/Loading';
import Failure from 'builder/scenes/Failure';
import env from 'builder/config';
import { Location } from 'history';
import styled, { ThemeProvider } from 'styled-components';
import PageTemplatesRoutes from 'builder/scenes/PageTemplates/PageTemplatesRoutes';
import { resolveLegacyThemeFromSite } from 'builder/util/resolveTheme';
import { horizonOptumTheme } from 'builder/scenes/SiteBuilder/SiteStylesPanel/BrandThemes';

export const StyledLoadingWrapper = styled.span`
  & > div > h6 {
    font-family: OptumSans;
  }
`;

export interface AuthenticatedRouterProps extends RouteProps {
  user?: User;
  logoutPending?: boolean;
  loginPending?: boolean;
  isLoadingUser: boolean;
  builderPermissions?: string[];
  initiateLogin: () => void;
  fetchSites: () => void;
  fetchSitesByUuid: () => void;
  fetchUserTenancy: () => Promise<string>;
  sites: Array<Site>;
  sitesMeta: StateMeta;
  userTenancy: string;
  location?: Location;
}

class AuthenticatedRouter extends PureComponent<AuthenticatedRouterProps> {
  private redirectInProgress = false;

  private hasAccess = () => true;

  private checkUser = () => {
    const {
      user,
      logoutPending,
      loginPending,
      location,
      isLoadingUser,
    } = this.props;

    if (user && !user.expired && user.expires_at > Date.now() / 1000) {
      if (!this.hasAccess() && !this.redirectInProgress) {
        this.redirectInProgress = true;
        toast(
          "You don't have access to this application, redirecting to access request page...",
          {
            onClose: () => {
              window.location.href = env.requestAccessUrl;
            },
            type: 'warning',
            autoClose: 3000,
            theme: 'colored',
          }
        );
      }
    } else if (!isLoadingUser && !logoutPending && !loginPending) {
      this.startLogin(location as Location);
    }
  };

  private startLogin = (location: Location) => {
    persistLocation(location);
    this.props.initiateLogin();
    userManager.signinRedirect();
  };

  componentDidMount() {
    this.checkUser();
    this.loadSites();
  }

  componentDidUpdate() {
    this.checkUser();
    this.loadSites();
  }

  private loadSites = () => {
    const { user, sitesMeta } = this.props;
    if (
      user &&
      this.hasAccess() &&
      !sitesMeta.pending &&
      !sitesMeta.successful
    ) {
      this.props.fetchSitesByUuid();
    }
  };

  /** Resolve theme from URL (/sites/:id) or fallback to a safe default. */
  private getAppliedTheme(): Theme {
    const { location, sites } = this.props;
    const match = location?.pathname?.match(/\/sites\/(\d+)/);
    const siteId = match ? parseInt(match[1], 10) : undefined;

    if (siteId && Array.isArray(sites) && sites.length) {
      const site = sites.find(s => s.siteId === siteId);
      if (site?.theme) return resolveLegacyThemeFromSite(site.theme);
    }

    // For /sites (no :id) and other routes
    return resolveLegacyThemeFromSite(horizonOptumTheme);
  }

  /** Merge the resolved site theme over the base theme from App.tsx */
  private mergeTheme = (outer: any): any => {
    const applied = this.getAppliedTheme();
    return {
      ...outer,
      ...applied,
      colors: {
        ...(outer?.colors || {}),
        ...(applied?.colors || {}),
        aux: { ...(outer?.colors?.aux || {}), ...(applied?.colors?.aux || {}) },
      },
      font: {
        ...(outer?.font || {}),
        ...(applied?.font || {}),
      },
    };
  };

  render() {
    const {
      user,
      logoutPending,
      loginPending,
      sitesMeta,
      sites,
      isLoadingUser,
    } = this.props;

    return (
      <ThemeProvider theme={this.mergeTheme}>
        {user && this.hasAccess() && sitesMeta.successful ? (
          <Switch>
            <Route path="/sites" exact component={SiteList} />
            <Route
              path="/sites/:siteId"
              render={props => {
                const siteId = parseInt(props.match.params.siteId, 10);

                if (
                  isNaN(siteId) ||
                  (env.permissionsEnabled &&
                    !sites.some(
                      site => site.siteId === siteId && site.access.read
                    )) ||
                  (!env.permissionsEnabled &&
                    !sites.some(site => site.siteId === siteId))
                ) {
                  return <Redirect to="/sites" />;
                }

                return <SiteRouter siteId={siteId} {...props} />;
              }}
            />
            <Route path="/forms" exact component={SiteList} />
            <Route
              path="/forms/:formName"
              render={props => {
                const formName = props.match.params.formName;
                if (!formName) return <Redirect to="/forms" />;
                return <SiteList {...props} />;
              }}
            />
            <Route path="/page-templates" exact component={SiteList} />
            <Route
              path="/page-templates/:pageTemplateId"
              render={props => {
                if (!props.match.params.pageTemplateId)
                  return <Redirect to="/page-templates" />;
                return <PageTemplatesRoutes />;
              }}
            />
            <Route
              path="/page-templates/:pageTemplateId/preview"
              render={props => {
                if (!props.match.params.pageTemplateId)
                  return <Redirect to="/page-templates" />;
                return <PageTemplatesRoutes />;
              }}
            />
            <Route>
              <Redirect to="/sites" />
            </Route>
          </Switch>
        ) : logoutPending ? (
          <StyledLoadingWrapper>
            <Loading message="Logging Out" />
          </StyledLoadingWrapper>
        ) : isLoadingUser || loginPending || !this.hasAccess() ? (
          <StyledLoadingWrapper>
            <Loading message="Authenticating" />
          </StyledLoadingWrapper>
        ) : sitesMeta.pending ? (
          <StyledLoadingWrapper>
            <Loading message="Loading sites" />
          </StyledLoadingWrapper>
        ) : (
          <Failure />
        )}
      </ThemeProvider>
    );
  }
}

export default AuthenticatedRouter;
